import React from 'react'
import { captureException } from '@sentry/browser'
import { DateTime } from 'luxon'

import Content from './content'
import { TicketsAppContext } from '../context/tickets-app-context'
import { OldCartContext } from '../context/old-cart-context'
import {
  CartIcon,
  Contact,
  Loading,
  CartExpiration
} from '../../shared/components'
import {
  getEvents,
  getCart,
  postItemsToCart,
  deleteItemsFromCart,
  deleteItem
} from '../../services/api'
import { getPlanAheadPricing } from '../helpers/calendar'
import params from '../../services/params'
import storage from '../../services/storage'
import { CartSummary } from '../components'
import {
  buildCartEvent,
  getCurrentChosenEvent,
  getSessionCartData,
  prepareItemsForCart,
  getAddOnsInNeedOfRestore
} from '../helpers/cart-builder'
import {
  dateFromQuery,
  setFromEventSessionData,
  clearTicketsAppSessionData,
  shouldClearSession,
  queryEventOrDateChanged,
  queryEventNotChosenEvent,
  inManualUpdate,
  totalTickets,
  onSameDay,
  isStandalone,
  findAddOnsFor,
  isExpired,
  notMembershipType,
  getPricingObject,
  hasIllinoisTickets
} from '../helpers/logic'

import { STEPS, PATHS } from '../helpers/steps'

export const Direction = {
  FORWARDS: 'forwards',
  BACKWARDS: 'backwards'
}

const TEST_CURRENT_DATE = storage.getItem('testCurrentDate')
let TEST_JS_DATE = null
if (TEST_CURRENT_DATE) {
  const [year, month, day] = TEST_CURRENT_DATE.split('-')
  TEST_JS_DATE = new Date(year, month - 1, day)
}

export default class TicketsApp extends React.Component {
  initialize = async () => {
    let [fromEvent, queryDate, queryExternalId, exchange] = params.getAll(
      'event_id',
      'date',
      'external_id',
      'exchange'
    )
    shouldClearSession(fromEvent) && clearTicketsAppSessionData()
    let { day, step, chosenEventId, fromEventId, minimumAvailability } =
      getSessionCartData()
    let selectedDate = day ? new Date(day) : null
    let initialDate = queryDate ? dateFromQuery(queryDate) : null
    let chosenEvent = null
    let fromEventChange = queryEventOrDateChanged(
      initialDate,
      selectedDate,
      fromEvent,
      fromEventId
    )
    let events,
      firstAvailableDate = null
    try {
      let {
        items,
        membership,
        expiresAt,
        cleared,
        id: cartId,
        exchangeCredit,
        exchangeDetails
      } = await getCart()

      if (cleared || (chosenEventId && items.length === 0)) {
        step = STEPS.PARTY
        storage.setItem('step', step)
      }

      // if there is no step set in the session we default to DATETICKETS step
      if (!step) {
        step = STEPS.DATETICKETS
      }

      if (
        fromEventChange ||
        (!inManualUpdate && fromEvent && step !== STEPS.ADDONS)
      ) {
        step = null
        // make sure there is no chosenEvent yet
        chosenEventId = null
        // selectedDate will get set to query param date if it exists
        selectedDate = initialDate ? initialDate : null
        // set all of the session data with the newly collected data
        setFromEventSessionData(fromEvent, step, selectedDate)
      }

      if (fromEvent && initialDate && queryExternalId) {
        selectedDate = initialDate
        chosenEventId = queryExternalId
        step = STEPS.PARTY
        fromEvent = null
        storage.setItem('day', selectedDate)
        storage.setItem('chosenEventId', chosenEventId)
      }

      if (fromEvent && !selectedDate) {
        // getPlanAheadPricing will keep checking for available dates up to 9 times
        // this needs to happen here to get events which is used to populate the party picker page
        let startDate = TEST_JS_DATE
          ? DateTime.fromJSDate(TEST_JS_DATE)
          : DateTime.fromJSDate(new Date(), { zone: 'America/Chicago' })
        let minTix = minimumAvailability || 1
        ;({ firstAvailableDate } = await getPlanAheadPricing(
          startDate,
          fromEvent,
          minTix
        ))
        ;({ events } = await getEvents(firstAvailableDate, fromEvent))
      }

      if (selectedDate && !events) {
        ;({ events } = await getEvents(selectedDate))
      }

      if (queryEventNotChosenEvent(fromEvent, chosenEventId)) {
        if (events) {
          const fromEventCalendarEvent = events.find(
            event => event.productId === Number(fromEvent)
          )

          if (fromEventCalendarEvent.times.length !== 0) {
            chosenEventId = fromEventCalendarEvent.times[0].id
          } else {
            chosenEventId = fromEventCalendarEvent.id
          }

          if (fromEventCalendarEvent.managedCapacity) {
            step = step && step > STEPS.PARTY ? step : STEPS.PARTY
          }
        }
        setFromEventSessionData(fromEvent, step)
      }

      if (inManualUpdate) {
        let cartItem = items.find(
          item => item.externalId === Number(queryExternalId)
        )

        /*
        Set the chosenZoneId to the zone associated with the first price type.  If the display multiple zones flag is set to true, then
          we know the user had to have selected a zone when adding tickets and that we need to reselect this zone again.
          TODO: Need to figure out if multiple zones becomes an issue when we finish implementing the rest of the zone logic.
         */
        if (cartItem.displayMultipleZones === true) {
          storage.setItem('chosenZoneId', cartItem.tickets[0].zoneId)
        }

        // we give buildCartEvent an empty items array to make all ticket counts show
        // zero in the initial manual update flow to make the user do less work
        // this also allows us to update the event without having to delete it
        await this.setOldCart(items, cartItem)

        chosenEvent = buildCartEvent(
          [],
          getCurrentChosenEvent(
            chosenEventId,
            events,
            this.oldCart.manualMaximum
          )
        )
      }

      // if there is a chosen event id set we want to build a
      // chosen event object that has all necessary attributes
      if (!chosenEvent && chosenEventId && events) {
        chosenEvent = buildCartEvent(
          items,
          getCurrentChosenEvent(chosenEventId, events)
        )
      }

      if (chosenEvent) {
        storage.setItem('chosenEventId', chosenEvent.id)
      }

      this.setState({
        events: { events, loading: false },
        membership,
        selectedDate,
        chosenEvent,
        fromEvent,
        minimumAvailability: minimumAvailability ? minimumAvailability : 1,
        cart: {
          items,
          expiresAt,
          exchangeCredit,
          exchangeDetails,
          id: cartId,
          loading: false
        },
        step: step ? Number(step) : 1,
        loading: false,
        error: null,
        exchangeId: exchange
      })
    } catch (e) {
      const error = new Error(e.message)
      error.code = 'contact'
      if (process.env.NODE_ENV === 'production') {
        captureException(error)
      }
      this.setError(error)
    }
  }

  setOldCart = async (items, cartItem) => {
    let storageOldCartItemIds = storage.getItem('oldCartItemIds')
    let manualMaximum = storage.getItem('manualMaximum')
    let oldCartItemIds =
      storageOldCartItemIds && JSON.parse(storageOldCartItemIds)
    let originalCartAddOns = []
    if (!oldCartItemIds) {
      originalCartAddOns = findAddOnsFor(cartItem, items)
      oldCartItemIds = originalCartAddOns.map(item => item.externalId)
      storage.setItem('oldCartItemIds', JSON.stringify(oldCartItemIds))
    } else {
      originalCartAddOns = items
        .filter(notMembershipType)
        .filter(item => oldCartItemIds.includes(item.externalId))
    }
    if (manualMaximum) {
      manualMaximum = Number(manualMaximum)
    } else {
      manualMaximum = totalTickets(cartItem.tickets)
      storage.setItem('manualMaximum', manualMaximum)
    }

    const oldCart = {
      addOnItemIds: oldCartItemIds,
      originalCartAddOns,
      manualMaximum
    }

    this.oldCart = oldCart
  }

  setError = (error, stateChanges = {}, clearSession = true) => {
    // if we experience an error we
    // warn
    // clear session data
    let newError = null
    if (error) {
      const showError =
        error.code && ['api', 'client', 'contact'].includes(error.code)
      let baseError = new Error('We are having trouble with this event')
      baseError.code = 'contact'
      console.warn(error)
      clearSession && clearTicketsAppSessionData()
      newError = showError ? error : baseError
    }
    this.setState({
      error: newError,
      loading: false,
      ...stateChanges
    })
  }

  goToCart = () => {
    const { cartPath } = this.props
    if (isExpired(this.state.cart.expiresAt)) {
      return this.recoverFromExpiration()
    }
    location.assign(cartPath)
  }

  setMinimumAvailability = minimumAvailability => {
    storage.setItem('minimumAvailability', minimumAvailability)
    this.setState({ minimumAvailability })
  }

  selectDay = async day => {
    storage.setItem('day', day)
    if (day !== this.state.selectedDate) {
      this.setState(prevState => ({
        selectedDate: day,
        events: { ...prevState.events, loading: true }
      }))

      const eventsData = await getEvents(day)
      const events = eventsData ? eventsData.events : []

      let eventsState = {}

      if (events) {
        eventsState = {
          events,
          loading: false
        }
      }

      this.setState({
        events: eventsState
      })
    }
  }

  clearChosenEvent = async () => {
    let {
      cart,
      chosenEvent,
      events: { events },
      fromEvent
    } = this.state
    const addedCartEvent = cart.items.find(
      item => item.externalId === chosenEvent.id
    )
    let newCartItems = cart.items
    let itemsToDelete = [addedCartEvent]
    // we sometimes get into situations where the cart has been cleared and need to short circuit
    if (!addedCartEvent) {
      return
    }

    if (!isStandalone(addedCartEvent)) {
      const GA = cart.items.find(item => onSameDay(item, addedCartEvent))
      if (GA) {
        itemsToDelete.push(GA)
      }
      ;({ items: newCartItems } = await deleteItemsFromCart(itemsToDelete))
    } else {
      ;({ items: newCartItems } = await deleteItemsFromCart(itemsToDelete))
    }

    if (!fromEvent) {
      //if we don't have fromEvent in state double check that it's not in the query params
      fromEvent = params.get('event_id')
    }

    const fromEventEvent = fromEvent
      ? events.find(event => event.productId === Number(fromEvent))
      : null

    if (fromEventEvent && chosenEvent) {
      const pricing = getPricingObject(fromEventEvent, chosenEvent.id)
      chosenEvent = { ...fromEventEvent, tickets: chosenEvent.tickets, pricing }
    } else {
      chosenEvent = null
    }

    this.setState({
      chosenEvent,
      cart: { ...cart, items: newCartItems }
    })
  }

  getPathway = () => {
    const { fromEvent, chosenEvent } = this.state
    let pathway = PATHS.FROMEE
    if (fromEvent && chosenEvent && !chosenEvent.managedCapacity) {
      pathway = PATHS.DEFAULT
    } else if (!fromEvent) {
      pathway = PATHS.DEFAULT
    }
    return pathway
  }

  back = async () => {
    let { step: currentStep, fromEvent, selectedDate, chosenEvent } = this.state
    let pathway = this.getPathway()
    if (currentStep === STEPS.ADDONS || currentStep === STEPS.PARTY) {
      this.setState({ loading: true })
      if (currentStep === STEPS.ADDONS) {
        if (inManualUpdate) {
          await this.restoreAddOns()
        } else {
          await this.clearAddOns()
        }
      }

      if (
        currentStep === STEPS.PARTY ||
        (fromEvent && currentStep == STEPS.ADDONS)
      ) {
        await this.clearChosenEvent()
      }
    }

    if (currentStep === STEPS.DATETICKETS && fromEvent) {
      selectedDate = null
      if (chosenEvent && hasIllinoisTickets(chosenEvent.tickets)) {
        this.setState({ loading: true })
        clearTicketsAppSessionData()
        await this.initialize()
      }
    }

    const direction = Direction.BACKWARDS

    const step = pathway[pathway.indexOf(currentStep) - 1]
    storage.setItem('step', step)
    this.setState({ step, direction, selectedDate, loading: false })
  }

  nextStep = async () => {
    const {
      step,
      cart: { expiresAt }
    } = this.state
    if (isExpired(expiresAt)) {
      await this.recoverFromExpiration()
    }
    let pathway = this.getPathway()
    const nextStep = pathway[pathway.indexOf(step) + 1]
    storage.setItem('step', nextStep)
    this.setState({
      step: nextStep,
      direction: Direction.FORWARDS,
      error: null
    })
  }

  setStep = async step => {
    this.setState({ loading: true })
    let currentEvent = this.state.chosenEvent
    let currentStep = this.state.step
    if (currentStep === STEPS.ADDONS) {
      if (inManualUpdate) {
        await this.restoreAddOns()
      } else {
        await this.clearAddOns()
      }
    }
    if (currentStep > STEPS.PARTY && step < STEPS.PARTY) {
      await this.clearChosenEvent()
    }
    if (step < STEPS.PARTY) {
      currentEvent = null
    }
    this.setState(currentStep => ({
      step,
      chosenEvent: currentEvent,
      direction: step > currentStep ? Direction.FORWARDS : Direction.BACKWARDS,
      error: null,
      loading: false
    }))
  }

  setCart = newCart => {
    this.setState(currentState => ({
      cart: { ...currentState.cart, ...newCart }
    }))
  }

  previewEvent = event => {
    this.setState({ previewedEvent: event })
  }

  selectEvent = (event, stopNext = false) => {
    let cartEvent = event
    storage.setItem('chosenEventId', cartEvent.id)

    this.setState(prevState => {
      let {
        chosenEvent,
        cart: { items }
      } = prevState
      if (
        !chosenEvent ||
        cartEvent.id !== chosenEvent.id ||
        cartEvent.chosenZone !== chosenEvent.chosenZone
      ) {
        cartEvent = buildCartEvent(items, cartEvent)
      } else {
        cartEvent = buildCartEvent(items, chosenEvent)
      }
      cartEvent.chosenZone
        ? storage.setItem('chosenZoneId', cartEvent.chosenZone.zoneId)
        : storage.removeItem('chosenZoneId')

      return {
        chosenEvent: cartEvent
      }
    })
    !stopNext && this.nextStep()
  }

  setChosenEvent = chosenEvent => {
    this.setState({ chosenEvent })
  }

  updateItem = cartItem => {
    this.setState(currentState => {
      const { chosenEvent, chosenAddOn } = currentState
      if (chosenAddOn) {
        return {
          chosenAddOn: { ...chosenAddOn, ...cartItem }
        }
      }
      return {
        chosenEvent: { ...chosenEvent, ...cartItem }
      }
    })
  }

  recoverFromExpiration = async () => {
    const { step, cart } = this.state
    // this is to make sure we only move users to the cart
    // if they are at a step that shows cart expiration
    this.setState({ cart: { ...cart, loading: true } })
    if (!inManualUpdate && step <= STEPS.PARTY) {
      const newCart = await getCart()
      this.setState({
        cart: { ...newCart, loading: false }
      })
      return
    } else {
      location.assign('/cart?cleared=true')
    }
  }

  addItemsToCart = async items => {
    try {
      if (isExpired(this.state.cart.expiresAt)) {
        await this.recoverFromExpiration()
      }
      this.setState(currentState => ({
        cart: { ...currentState.cart, error: null, loading: true }
      }))
      let currentCart = this.state.cart
      const itemsToAdd = prepareItemsForCart(items)
      currentCart = await postItemsToCart(itemsToAdd, currentCart.id)
      this.setState({
        cart: { ...currentCart, loading: false }
      })
      return true
    } catch (e) {
      console.warn(e)
      this.addItemsToCartFailure(e)
      return false
    }
  }

  addItemsToCartFailure = async error => {
    let newCart = null
    let currentError = error
    try {
      newCart = await getCart()
    } catch (e) {
      console.warn(error)
      currentError = e
    } finally {
      let { step, fromEvent, cart } = this.state
      let newStep = step === STEPS.PARTY ? step - 1 : step
      newCart = newCart || cart
      if (fromEvent) {
        newStep = step === STEPS.EVENTS ? step - 1 : step
      }
      const stateChanges = {
        cart: { ...newCart, loading: false },
        step: newStep
      }
      this.setError(currentError, stateChanges, false)
    }
  }

  selectAddOn = event => {
    let {
      cart: { items },
      selectedDate
    } = this.state
    // we set an empty items array to make all ticket counts show
    // zero in the manual update flow in order to let the chosenEvent
    // set the maximums correctly once it is updated in the party step
    if (inManualUpdate) {
      items = []
    }
    const chosenAddOn = buildCartEvent(items, event, selectedDate)
    this.setState({
      chosenAddOn
    })
  }

  clearAddOn = () => {
    this.setState({
      chosenAddOn: null
    })
  }

  restoreAddOns = async () => {
    const addOnsToRestore = getAddOnsInNeedOfRestore(
      this.oldCart.originalCartAddOns,
      this.state.cart.items
    )

    if (addOnsToRestore.length !== 0) {
      try {
        this.setState({
          cart: await postItemsToCart(addOnsToRestore, this.state.cart.id)
        })
      } catch (e) {
        this.setError(e)
      }
    }
  }

  clearAddOns = async () => {
    let cartAddOns = findAddOnsFor(
      this.state.chosenEvent,
      this.state.cart.items
    )
    cartAddOns = cartAddOns.filter(
      addOn => addOn.externalId !== this.state.chosenEvent.id
    )
    if (cartAddOns.length !== 0) {
      await this.deleteCartItems(cartAddOns)
    }
  }

  deleteCartItem = async itemId => {
    try {
      this.setState({ cart: await deleteItem(itemId), error: null })
    } catch (error) {
      this.setError(error)
    }
  }

  deleteCartItems = async items => {
    try {
      this.setState({ cart: await deleteItemsFromCart(items), error: null })
    } catch (error) {
      this.setError(error)
    }
  }

  showCartExpiration = () => {
    const { step } = this.state
    return step === STEPS.ADDONS
  }

  renderError = error => {
    if (error) {
      let errorElement = <p className="font-bold text-16">{error.message}</p>
      if (error && error.code === 'contact') {
        errorElement = (
          <Contact
            preText="We are having trouble with this event."
            email="ContactUs@sheddaquarium.org"
            phone="312-939-2438"
          />
        )
      }
      return errorElement
    } else {
      return null
    }
  }

  setAddOnsInNeedOfUpdate = addOnsInNeedOfUpdate => {
    this.setState({ addOnsInNeedOfUpdate })
  }

  oldCart = {
    addOnItemIds: [],
    manualMaximum: null
  }

  state = {
    loading: true,
    cart: {
      id: 1,
      items: [],
      membership: null,
      loading: false
    },
    step: 1,
    direction: Direction.FORWARDS,
    events: { events: [], loading: false },
    membership: null,
    selectedDate: null,
    chosenEvent: null,
    chosenAddOn: null,
    error: null,
    minimumAvailability: 1,
    remainingTickets: 0,
    exchangeId: null,
    actions: {
      selectDay: this.selectDay,
      selectEvent: this.selectEvent,
      setChosenEvent: this.setChosenEvent,
      updateItem: this.updateItem,
      addItemsToCart: this.addItemsToCart,
      selectAddOn: this.selectAddOn,
      clearAddOn: this.clearAddOn,
      nextStep: this.nextStep,
      setMinimumAvailability: this.setMinimumAvailability,
      clearChosenEvent: this.clearChosenEvent,
      deleteCartItem: this.deleteCartItem,
      setError: this.setError,
      back: this.back,
      setStep: this.setStep,
      deleteCartItems: this.deleteCartItems,
      goToCart: this.goToCart,
      setCart: this.setCart
    }
  }

  componentDidMount() {
    this.initialize()
  }

  render() {
    const {
      error,
      loading,
      cart: { items, expiresAt }
    } = this.state
    return (
      <React.Fragment>
        <CartIcon items={items} />
        <TicketsAppContext.Provider value={this.state}>
          <OldCartContext.Provider value={this.oldCart}>
            <div className="ecomm-container">
              <div className="ecomm-main">
                <div className="ecomm-main__inner">
                  {this.renderError(error)}
                  {loading ? <Loading /> : <Content />}
                </div>
              </div>

              <div className="ecomm-sidebar">
                <div className="ecomm-sidebar__inner flex flex-col-reverse md:block">
                  {this.showCartExpiration() && (
                    <CartExpiration expiresAt={expiresAt} />
                  )}
                  <CartSummary />
                </div>
              </div>
            </div>
          </OldCartContext.Provider>
        </TicketsAppContext.Provider>
      </React.Fragment>
    )
  }
}
